import { GROUP_TYPE } from "@/constants/area-constants";
import { FC } from "react";
import { useSelector } from "react-redux";
import TableTab from "../components/tabs/table-tab";
import { ACCESS_GROUPS } from "../constants/menu-types";
import { useLocale } from "../contexts/localeContext";
import { TableContextProvider } from "../contexts/table/TableContextProvider";
import TableContextStateMonitor from "../contexts/table/TableContextStateMonitor";
import { TranslationKeys } from "../contexts/TranslationKeys";
import { Category } from "../models/Category";
import columns from "../models/ColumnDefinitions/AccessGroupColumns";
import { removeColumns } from "../models/utilities/columnHelper";
import { ListQuery } from "../models/ListQuery";
import { Lookup } from "../models/Lookup";
import LookupTypes from "../models/LookupTypes";
import AccessAreaSearch from "../models/search/AccessAreaSearch";
import searchColumns from "../models/search/AccessGroupSearch";
import AccessTimePatternSearch from "../models/search/AccessTimePatternSearch";
import SiteSearch from "../models/search/SiteSearch";
import { DeleteEntityResult } from "../models/services/DeleteEntityResult";
import { getAccessAreaNamesWithPagination } from "../services/accessAreaService";
import { deleteAccessGroup, getAllAccessGroups } from "../services/accessGroupService";
import { getAccessTimePatternNames } from '../services/accessTimePatternService';
import { getSiteNamesWithPagination } from "../services/siteService";
import { getDayNames } from "../utilities/DateUtils";
import { ACCESS_TYPES, getAccessGroupPath } from "@/constants/access-constants";

interface Props {
    setBusy: (isBusy: boolean) => void,
    categories: Category[],
    setCategories: (category: Category[]) => void,
    onFilterChange?: (filter: any) => void,
    accessType: GROUP_TYPE;
}

const AccessTab: FC<Props> = ({ setBusy, categories, setCategories, onFilterChange, accessType }) => {
    const { translate } = useLocale();
    const isOssGroup = accessType === ACCESS_TYPES.OSS_GROUP;
    const itemPath = getAccessGroupPath(accessType);
    const userLoginId = useSelector((state: any) => (state.userInfo.id))
    const lookup: Lookup[] = [
        {
            name: LookupTypes.site,
            values: [],
            dataMethod: getSiteNamesWithPagination,
            searchAccessor: SiteSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.accessTimePattern,
            values: [],
            dataMethod: getAccessTimePatternNames,
            searchAccessor: AccessTimePatternSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.area,
            values: [],
            dataMethod: (listQuery?: ListQuery) => getAccessAreaNamesWithPagination(accessType, listQuery),
            searchAccessor: AccessAreaSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.days,
            values: getDayNames(translate)
        }
    ];

    const getTitle = (type: number | null) => {
        if (type === null) return "";

        switch (type) {
            case ACCESS_TYPES.ACCESS_GROUP:
                return translate.byKey(TranslationKeys.accessGroups);
            case ACCESS_TYPES.FLOOR_GROUP:
                return translate.byKey(TranslationKeys.floorGroups);
            case ACCESS_TYPES.OSS_GROUP:
                return translate.byKey(TranslationKeys.ossGroups);
            default:
                return "";
        }
    }
    const title = getTitle(accessType);

    const onDelete = (id: string): Promise<DeleteEntityResult> => {
        setBusy(true);
        return new Promise((resolve, reject) => {
            deleteAccessGroup(id)
                .then(
                    deleteResult => {
                        resolve(deleteResult);
                        const newCatergories: Category[] = categories.map((category) => {
                            return category.id === 0 ? {...category, count: (category?.count ?? 1) - 1,}: category;
                        });
                        setCategories(newCatergories);
                        setBusy(false);
                    },
                    deleteResult => {
                        reject(deleteResult);
                        setBusy(false);
                    })
        });
    }

    const initialiseUserPreferences = (setDetails: (userLoginId, ACCESS_GROUPS) => void) => {
        setDetails(userLoginId, ACCESS_GROUPS);
    }

    const getCyName = (type: number | null) => {
        const defaultCyName = "accessGroup";
        if (type === null) return defaultCyName;

        switch (type) {
            case ACCESS_TYPES.ACCESS_GROUP:
                return "accessGroup";
            case ACCESS_TYPES.FLOOR_GROUP:
                return "floorGroup";
            case ACCESS_TYPES.OSS_GROUP:
                return "ossGroup";
            default:
                return defaultCyName;
        }
    }

    return <TableContextProvider
        dataMethod={(listParameters?: ListQuery) => getAllAccessGroups(accessType, translate, listParameters)}
        initialiser={(setDetails) => initialiseUserPreferences(setDetails)}
        tableDescriptor={isOssGroup ? removeColumns(columns, ["area"]) : columns}
        searchDescriptor={searchColumns}
        useTable
        usePagination
        useFilter
        useSearch
        useSort
        deleteMethod={onDelete}
        lookup={lookup}
        customDelete={{ fieldName: "isDefault", comparisonValue: false }}>
        <TableContextStateMonitor onFilterChange={onFilterChange}/>
        <TableTab
            title={title}
            selectRows={false}
            rowPath={itemPath}
            cyName={getCyName(accessType)} />
    </TableContextProvider>;
};

export default AccessTab;
